<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { locale, _ } from 'svelte-i18n';
  import type { TreeDocument, CategoryNode, Category } from '../api';
  import { browseTreeStore, mergeCategoriesFlat, sortDocuments } from '../stores/browseTreeStore';
  import type { SortOrder } from '../stores/browseTreeStore';
  import { householdsStore } from '../stores/householdsStore';
  import { userSettings } from '../stores/settingsStore';
  import { categoriesStore } from '../stores/categoriesStore';
  import { defaultColumns, resolveColumns, resolveSubject } from './treeGridColumns';
  import type { TreeGridColumn } from './treeGridColumns';
  import {
    ChevronRight,
    ChevronDown,
    FileText,
    Users,
    Folder,
    FolderOpen,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
  } from 'lucide-svelte';

  function getDocumentUrl(doc: TreeDocument): string {
    const identifier = doc.slug || doc.id;
    return `/documents/${identifier}`;
  }

  function handleDocumentClick(e: MouseEvent, doc: TreeDocument) {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    navigate(getDocumentUrl(doc));
  }

  function resolveMemberName(memberId: string): string {
    if (memberId === '(Unassigned)') return $_('pages.browse.unassigned');
    for (const household of $householdsStore) {
      const member = household.members?.find((m) => m.id === memberId);
      if (member) return member.name;
    }
    // Unresolvable id (e.g. member deleted, or store/tree load race): show a
    // localized placeholder instead of a raw UUID (#289 W3, PO Finding #2).
    return $_('pages.browse.unknown_member');
  }

  function getLocalizedName(node: {
    categoryDisplayName?: string;
    categoryDisplayNameEn?: string;
    subCategoryDisplayName?: string;
    subCategoryDisplayNameEn?: string;
  }): string {
    const isEn = $locale?.startsWith('en');
    if ('categoryDisplayName' in node) {
      return isEn && node.categoryDisplayNameEn
        ? node.categoryDisplayNameEn
        : node.categoryDisplayName || '';
    }
    if ('subCategoryDisplayName' in node) {
      return isEn && node.subCategoryDisplayNameEn
        ? node.subCategoryDisplayNameEn
        : node.subCategoryDisplayName || '';
    }
    return '';
  }

  function handleSort(key: string) {
    if ($browseTreeStore.sortBy === key) {
      const next: SortOrder = $browseTreeStore.sortOrder === 'desc' ? 'asc' : 'desc';
      browseTreeStore.setSort(key, next);
    } else {
      browseTreeStore.setSort(key, 'desc');
    }
  }

  function getSortedDocs(docs: TreeDocument[]): TreeDocument[] {
    return sortDocuments(docs, $browseTreeStore.sortBy, $browseTreeStore.sortOrder);
  }

  /**
   * Resolves the column list for a category (Spec 010 E6):
   * 1. categoriesStore has the category with listColumns → resolveColumns (production path).
   * 2. Fallback (store empty / load error): defaultColumns — no guessing from data.
   *
   * ponytail: conservative fallback avoids showing unexpected columns when the
   * categories store fails to load; the production path always resolves category config.
   */
  function getCategoryColumns(
    categoryNode: CategoryNode,
    allCategories: Category[],
  ): TreeGridColumn[] {
    const storeCategory = allCategories.find((c) => c.name === categoryNode.category);
    if (storeCategory) return resolveColumns(storeCategory);
    return defaultColumns;
  }

  $: isPersonMode = $browseTreeStore.groupMode === 'person';
  $: flatCategories = isPersonMode ? [] : mergeCategoriesFlat($browseTreeStore.treeData);
  const colCount = defaultColumns.length;
  $: allCategories = $categoriesStore as Category[];
</script>

<div class="bg-surface border border-border rounded-lg p-3">
  <!-- Group by Person Toggle -->
  <div class="flex items-center justify-end gap-2 mb-3 pb-2 border-b border-border">
    <label class="flex items-center gap-2 cursor-pointer select-none">
      <span class="text-xs text-text-muted">{$_('pages.browse.group_by_person')}</span>
      <button
        role="switch"
        aria-checked={isPersonMode}
        aria-label={$_('pages.browse.group_by_person')}
        class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors
          {isPersonMode ? 'bg-accent' : 'bg-border'}"
        on:click={() => browseTreeStore.setGroupMode(isPersonMode ? 'flat' : 'person')}
      >
        <span
          class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform
            {isPersonMode ? 'translate-x-4' : 'translate-x-0'}"
        ></span>
      </button>
    </label>
  </div>

  {#if $browseTreeStore.treeData.length === 0}
    <p class="text-center text-text-muted py-8 text-sm">{$_('pages.browse.no_documents')}</p>
  {:else if isPersonMode}
    <!-- Person-grouped mode: single shared grid with defaultColumns -->
    <div class="grid" style="grid-template-columns: 1fr repeat({colCount - 1}, auto);">
      <!-- Header row -->
      {#each defaultColumns as col, i (col.key)}
        {@const isSorted = $browseTreeStore.sortBy === col.key}
        <div
          class="px-2.5 py-1.5 text-xs font-medium text-text-muted border-b border-border
            {col.hiddenClass || ''} {i === 0 ? '' : 'text-right'}"
        >
          {#if col.sortable}
            <button
              class="inline-flex items-center gap-1 hover:text-text transition-colors"
              on:click={() => handleSort(col.key)}
            >
              {$_(col.labelKey)}
              {#if isSorted && $browseTreeStore.sortOrder === 'asc'}
                <ArrowUp class="w-3 h-3" strokeWidth={1.5} />
              {:else if isSorted && $browseTreeStore.sortOrder === 'desc'}
                <ArrowDown class="w-3 h-3" strokeWidth={1.5} />
              {:else}
                <ArrowUpDown class="w-3 h-3 opacity-40" strokeWidth={1.5} />
              {/if}
            </button>
          {:else}
            {$_(col.labelKey)}
          {/if}
        </div>
      {/each}

      {#each $browseTreeStore.treeData as responsibleNode (responsibleNode.responsible)}
        <!-- Person header — spans full width -->
        <button
          class="flex items-center gap-2 w-full text-left px-2.5 py-2 rounded transition-colors hover:bg-surface-hover"
          style="grid-column: 1 / -1;"
          on:click={() => browseTreeStore.toggleResponsible(responsibleNode.responsible)}
        >
          {#if $browseTreeStore.expandedResponsible.has(responsibleNode.responsible)}
            <ChevronDown class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
          {:else}
            <ChevronRight class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
          {/if}
          <Users class="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
          <span class="flex-1 text-sm font-medium text-text"
            >{resolveMemberName(responsibleNode.responsible)}</span
          >
          <span class="text-xs text-text-muted bg-bg-alt px-2 py-0.5 rounded-full"
            >{responsibleNode.documentCount}</span
          >
        </button>

        {#if $browseTreeStore.expandedResponsible.has(responsibleNode.responsible)}
          {#each responsibleNode.categories as categoryNode (categoryNode.category)}
            {@const categoryKey = `${responsibleNode.responsible}/${categoryNode.category}`}
            <!-- Category header -->
            <button
              class="flex items-center gap-2 w-full text-left pl-8 pr-2.5 py-1.5 rounded transition-colors hover:bg-surface-hover"
              style="grid-column: 1 / -1;"
              on:click={() => browseTreeStore.toggleCategory(categoryKey)}
            >
              {#if $browseTreeStore.expandedCategory.has(categoryKey)}
                <ChevronDown class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                <FolderOpen class="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
              {:else}
                <ChevronRight class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                <Folder class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
              {/if}
              <span class="flex-1 text-sm font-medium text-text"
                >{getLocalizedName(categoryNode)}</span
              >
              <span class="text-xs text-text-muted bg-bg-alt px-2 py-0.5 rounded-full"
                >{categoryNode.documentCount}</span
              >
            </button>

            {#if $browseTreeStore.expandedCategory.has(categoryKey)}
              {#each categoryNode.subCategories as subCategoryNode (subCategoryNode.subCategory)}
                {@const subCategoryKey = `${categoryKey}/${subCategoryNode.subCategory}`}
                <!-- SubCategory header -->
                <button
                  class="flex items-center gap-2 w-full text-left pl-14 pr-2.5 py-1.5 rounded transition-colors hover:bg-surface-hover"
                  style="grid-column: 1 / -1;"
                  on:click={() => browseTreeStore.toggleSubCategory(subCategoryKey)}
                >
                  {#if $browseTreeStore.expandedSubCategory.has(subCategoryKey)}
                    <ChevronDown class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                    <FolderOpen class="w-3.5 h-3.5 text-accent shrink-0" strokeWidth={1.5} />
                  {:else}
                    <ChevronRight class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                    <Folder class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                  {/if}
                  <span class="flex-1 text-sm text-text">{getLocalizedName(subCategoryNode)}</span>
                  <span class="text-xs text-text-muted bg-bg-alt px-2 py-0.5 rounded-full"
                    >{subCategoryNode.documentCount}</span
                  >
                </button>

                {#if $browseTreeStore.expandedSubCategory.has(subCategoryKey)}
                  {#each getSortedDocs(subCategoryNode.documents) as doc (doc.id)}
                    {#each defaultColumns as col, ci (col.key)}
                      {#if ci === 0}
                        <a
                          href={getDocumentUrl(doc)}
                          class="flex items-center gap-2 text-left pl-20 pr-2.5 py-1.5 rounded transition-colors hover:bg-surface-hover group no-underline {col.hiddenClass ||
                            ''}"
                          on:click={(e) => handleDocumentClick(e, doc)}
                        >
                          <FileText
                            class="w-3.5 h-3.5 text-text-muted shrink-0 group-hover:text-accent"
                            strokeWidth={1.5}
                          />
                          <span class="text-sm text-text group-hover:text-accent truncate">
                            {col.format(doc, $userSettings)}
                          </span>
                        </a>
                      {:else}
                        <span
                          class="px-2.5 py-1.5 text-xs text-text-muted text-right self-center {col.hiddenClass ||
                            ''}"
                        >
                          {col.format(doc, $userSettings)}
                        </span>
                      {/if}
                    {/each}
                  {/each}
                {/if}
              {/each}
            {/if}
          {/each}
        {/if}
      {/each}
    </div>
  {:else}
    <!-- Flat mode: Category → SubCategory → Document, per-category grids (Spec 010 E6) -->
    {#each flatCategories as categoryNode (categoryNode.category)}
      {@const categoryKey = categoryNode.category}
      {@const cols = getCategoryColumns(categoryNode, allCategories)}
      {@const catColCount = cols.length}

      <!-- Category header button (outside any grid so it can be full width) -->
      <button
        data-testid="browse-node-{categoryNode.category}"
        class="flex items-center gap-2 w-full text-left px-2.5 py-2 rounded transition-colors hover:bg-surface-hover"
        on:click={() => browseTreeStore.toggleCategory(categoryKey)}
      >
        {#if $browseTreeStore.expandedCategory.has(categoryKey)}
          <ChevronDown class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
          <FolderOpen class="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
        {:else}
          <ChevronRight class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
          <Folder class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
        {/if}
        <span class="flex-1 text-sm font-medium text-text">{getLocalizedName(categoryNode)}</span>
        <span class="text-xs text-text-muted bg-bg-alt px-2 py-0.5 rounded-full"
          >{categoryNode.documentCount}</span
        >
      </button>

      {#if $browseTreeStore.expandedCategory.has(categoryKey)}
        <!-- Per-category grid — column count matches this category's column set (Spec 010 E6) -->
        <div class="grid" style="grid-template-columns: 1fr repeat({catColCount - 1}, auto);">
          <!-- Column headers for this category -->
          {#each cols as col, i (col.key)}
            {@const isSorted = $browseTreeStore.sortBy === col.key}
            <div
              class="px-2.5 py-1.5 text-xs font-medium text-text-muted border-b border-border
                {col.hiddenClass || ''} {i === 0 ? '' : 'text-right'}"
            >
              {#if col.sortable}
                <button
                  class="inline-flex items-center gap-1 hover:text-text transition-colors"
                  on:click={() => handleSort(col.key)}
                >
                  {$_(col.labelKey)}
                  {#if isSorted && $browseTreeStore.sortOrder === 'asc'}
                    <ArrowUp class="w-3 h-3" strokeWidth={1.5} />
                  {:else if isSorted && $browseTreeStore.sortOrder === 'desc'}
                    <ArrowDown class="w-3 h-3" strokeWidth={1.5} />
                  {:else}
                    <ArrowUpDown class="w-3 h-3 opacity-40" strokeWidth={1.5} />
                  {/if}
                </button>
              {:else}
                {$_(col.labelKey)}
              {/if}
            </div>
          {/each}

          {#each categoryNode.subCategories as subCategoryNode (subCategoryNode.subCategory)}
            {@const subCategoryKey = `${categoryKey}/${subCategoryNode.subCategory}`}
            <button
              class="flex items-center gap-2 w-full text-left pl-8 pr-2.5 py-1.5 rounded transition-colors hover:bg-surface-hover"
              style="grid-column: 1 / -1;"
              on:click={() => browseTreeStore.toggleSubCategory(subCategoryKey)}
            >
              {#if $browseTreeStore.expandedSubCategory.has(subCategoryKey)}
                <ChevronDown class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                <FolderOpen class="w-3.5 h-3.5 text-accent shrink-0" strokeWidth={1.5} />
              {:else}
                <ChevronRight class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
                <Folder class="w-3.5 h-3.5 text-text-muted shrink-0" strokeWidth={1.5} />
              {/if}
              <span class="flex-1 text-sm text-text">{getLocalizedName(subCategoryNode)}</span>
              <span class="text-xs text-text-muted bg-bg-alt px-2 py-0.5 rounded-full"
                >{subCategoryNode.documentCount}</span
              >
            </button>

            {#if $browseTreeStore.expandedSubCategory.has(subCategoryKey)}
              {#each getSortedDocs(subCategoryNode.documents) as doc (doc.id)}
                {#each cols as col, ci (col.key)}
                  {#if ci === 0}
                    <a
                      data-testid="browse-doc-{doc.id}"
                      href={getDocumentUrl(doc)}
                      class="flex items-center gap-2 text-left pl-14 pr-2.5 py-1.5 rounded transition-colors hover:bg-surface-hover group no-underline {col.hiddenClass ||
                        ''}"
                      on:click={(e) => handleDocumentClick(e, doc)}
                    >
                      <FileText
                        class="w-3.5 h-3.5 text-text-muted shrink-0 group-hover:text-accent"
                        strokeWidth={1.5}
                      />
                      <span class="text-sm text-text group-hover:text-accent truncate">
                        {col.format(doc, $userSettings)}
                      </span>
                    </a>
                  {:else if col.key === 'subject'}
                    <!-- Subject cell: responsive, width-capped, truncated, with tooltip (Spec 010 E7) -->
                    <span
                      class="px-2.5 py-1.5 text-xs text-text-muted self-center max-w-[14rem] truncate {col.hiddenClass ||
                        ''}"
                      title={resolveSubject(doc)}
                    >
                      {col.format(doc, $userSettings)}
                    </span>
                  {:else}
                    <span
                      class="px-2.5 py-1.5 text-xs text-text-muted text-right self-center {col.hiddenClass ||
                        ''}"
                    >
                      {col.format(doc, $userSettings)}
                    </span>
                  {/if}
                {/each}
              {/each}
            {/if}
          {/each}
        </div>
      {/if}
    {/each}
  {/if}
</div>
